/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useContext, useMemo } from 'react';
import type { EPSGCode } from '@opengeoweb/shared';
import type {
  LayerFoundation,
  TileServerSettings,
  TileSettings,
} from '@opengeoweb/webmap';
import { LayerType, tilesettings } from '@opengeoweb/webmap';
import { DefaultBaseLayers } from './DefaultBaseLayers';
import { TimeAwareEDRLocationLayer } from './TimeAwareEDRLocationLayer';
import { TimeAwareGeoTIFFLayer } from './TimeAwareGeoTIFFLayer';
import { TimeawareImageSourceWMSLayer } from './TimeawareImageSourceWMSLayer';
import { TimeAwareWMTSLayer } from './TimeAwareWMTSLayer';
import { WMSLayer } from './WMSLayer';
import { XYZLayer } from './XYZLayer';
import { styleNameToStyleLike } from '../../../edr-layer-api';
import { baseLayer } from '../../../layers/publicLayers';
import MapContext from '../context/MapContext';
import type { DrawModeExitCallback, FeatureEvent } from '../draw/types';
import type { OnInitializeLayerProps } from '../utils/types';

export interface OpenLayersLayerProps extends LayerFoundation {
  zIndex?: number;
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  onLayerReady?: (layerId: string) => void;
  onLayerError?: (layerId: string, message: string) => void;
  debugMode?: boolean;
  id: string;

  isInEditMode?: boolean;

  drawMode?: string;

  updateGeojson?: (geoJson: GeoJSON.FeatureCollection, reason: string) => void;

  exitDrawModeCallback?: (reason: DrawModeExitCallback) => void;

  selectedFeatureIndex?: number;

  onClickFeature?: (event?: FeatureEvent) => void;

  onHoverFeature?: (event: FeatureEvent) => void;
  tileServerSettings?: TileServerSettings;
}

export const OpenLayersLayer = (
  layerProps: OpenLayersLayerProps,
): React.ReactElement | null => {
  const {
    id,
    service,
    enabled,
    name,
    style,
    dimensions,
    opacity,
    zIndex,
    onInitializeLayer,
    onLayerReady,
    onLayerError,
    debugMode,
    layerType = LayerType.mapLayer, // Since there are now multiple layers types, we should set the default. Previously uninitialized meant mapLayer. Now we have to set it, because it could also be an edrLayer.
    tileServerSettings = tilesettings,
    options,
  } = layerProps;
  const isBaseLayer = layerType === LayerType.baseLayer;
  const isOverlay = layerType === LayerType.overLayer;
  const layerOptions = useMemo(() => {
    return {
      opacity: opacity !== null && opacity !== undefined ? opacity : 1,
      zIndex: zIndex || 0,
    };
  }, [opacity, zIndex]);

  const { map } = useContext(MapContext);
  // Check standard maplayers
  if (!isOverlay && !isBaseLayer) {
    switch (layerType) {
      case LayerType.mapLayer:
        return (
          <TimeawareImageSourceWMSLayer
            debugMode={debugMode}
            dimensions={dimensions}
            getCapsURL={service!}
            layerId={id}
            layerName={name!}
            layerOptions={layerOptions}
            onInitializeLayer={onInitializeLayer}
            onLayerError={onLayerError}
            onLayerReady={onLayerReady}
            styleName={style}
            visible={!!(enabled !== false)}
          />
        );
      case LayerType.wmtsMapLayer:
        return (
          <TimeAwareWMTSLayer
            debugMode={debugMode}
            dimensions={dimensions}
            getCapsURL={service!}
            layerId={id}
            layerName={name!}
            layerOptions={layerOptions}
            onInitializeLayer={onInitializeLayer}
            onLayerError={onLayerError}
            onLayerReady={onLayerReady}
            styleName={style}
            visible={!!(enabled !== false)}
          />
        );
      case LayerType.edrFeatureLayer: {
        const parameterNames = options?.parameterNames.join(',') || '';
        const multiParam =
          Array.isArray(options?.parameterNames) &&
          options.parameterNames.length > 1;
        const styleLike =
          style !== undefined
            ? styleNameToStyleLike(style, multiParam)
            : undefined;
        return (
          <TimeAwareEDRLocationLayer
            dimensions={dimensions}
            edrBaseUrl={service!}
            layerId={id}
            onInitializeLayer={onInitializeLayer}
            opacity={layerOptions.opacity}
            parameter={parameterNames}
            style={styleLike}
            visible={!!(enabled !== false)}
            zIndex={layerOptions.zIndex}
          />
        );
      }
      case LayerType.stacGeoTIFFLayer: {
        return (
          <TimeAwareGeoTIFFLayer
            dimensions={dimensions}
            layerId={id}
            layerName={id}
            onInitializeLayer={onInitializeLayer}
            opacity={layerOptions.opacity}
            stacCollectionUrl={service!}
            visible={!!(enabled !== false)}
            zIndex={layerOptions.zIndex}
          />
        );
      }
      default:
        return null;
    }
  }

  // Check the other layers
  if (isBaseLayer || isOverlay) {
    const { type, name, service, tileServer } = layerProps;
    if (type === 'twms' && name) {
      const tileSettingByName = tileServerSettings[name] || tileServer?.[name];
      if (!tileSettingByName) {
        console.warn(
          `Tilesetting with name ${name} not found in `,
          tileServerSettings,
          tileServer,
        );
      }

      const projectionName = map
        ?.getView()
        ?.getProjection()
        ?.getCode() as EPSGCode;

      const projectionUsedFromTileServer = tileSettingByName[projectionName]
        ? projectionName
        : 'EPSG:3857';

      const tileSettingForCRS =
        tileSettingByName &&
        ((projectionName &&
          tileSettingByName[projectionUsedFromTileServer]) as TileSettings);
      if (tileSettingForCRS) {
        const {
          home,
          tileServerType,
          urlTemplate: urlTemplateOverride,
        } = tileSettingForCRS;

        let urlTemplate = urlTemplateOverride?.(home) || '';
        if (!urlTemplateOverride) {
          switch (tileServerType) {
            case 'arcgisonline':
              urlTemplate = `${home}{z}/{y}/{x}.png`;
              break;
            case 'osm':
              urlTemplate = `${home}{z}/{x}/{y}.png`;
              break;
            case 'wmst':
              urlTemplate = `${home}{z}/{y}/{x}`;
              break;
            default:
              break;
          }
        }

        if (!urlTemplate) {
          console.warn(
            `Tile server type ${tileServerType} not supported`,
            tileServerSettings,
            tileServer,
          );
        }

        return (
          <XYZLayer
            layerOptions={{
              ...layerOptions,
              minZoom: tileSettingForCRS?.minLevel,
              maxZoom: tileSettingForCRS?.maxLevel,
            }}
            layerProps={{ ...layerProps, enabled: true }}
            tileProjectionCode={projectionUsedFromTileServer}
            urlTemplate={urlTemplate}
          />
        );
      }
    } else if (name && service) {
      return (
        <WMSLayer
          layerName={name!}
          layerOptions={layerOptions}
          layerProps={layerProps}
          styleName={style || 'default'}
          url={service!}
        />
      );
    } else if (baseLayer) {
      return <DefaultBaseLayers />;
    }
  }
  return <div />;
};
